/**
 * Visual Artifact Fixtures
 *
 * Small reusable sample artifacts for tests and local development.
 *
 * Primary responsibilities:
 * - Provide one fixture for each supported artifact type
 * - Keep sample payloads valid against runtime type guards
 *
 * Dependencies:
 * - Visual artifact types
 */

import type {
  AddressMapArtifact,
  BlueprintArtifact,
  HeatmapArtifact,
  RouteMapArtifact,
  SchematicArtifact,
  TerritoryMapArtifact,
  ThreeDModelArtifact,
  VisualArtifact
} from './types'

export const threeDModelArtifactFixture: ThreeDModelArtifact = {
  id: 'artifact-3d-model-office',
  type: '3d-model',
  title: 'Office floor concept',
  createdAt: '2026-07-04T12:00:00.000Z',
  data: {
    assetUrl: 'https://assets.example.com/artifacts/office-floor.glb',
    format: 'glb',
    previewImageUrl: 'https://assets.example.com/artifacts/office-floor.webp',
    unit: 'meter',
    bounds: {
      width: 12,
      height: 3.2,
      depth: 8
    }
  }
}

export const schematicArtifactFixture: SchematicArtifact = {
  id: 'artifact-schematic-intake',
  type: 'schematic',
  title: 'Client intake flow',
  createdAt: '2026-07-04T12:05:00.000Z',
  data: {
    nodes: [
      {
        id: 'start',
        label: 'Start',
        kind: 'entry',
        x: 0,
        y: 0
      },
      {
        id: 'review',
        label: 'Review',
        kind: 'decision',
        x: 160,
        y: 0
      },
      {
        id: 'book',
        label: 'Book',
        kind: 'action',
        x: 320,
        y: 0
      }
    ],
    edges: [
      {
        id: 'start-review',
        from: 'start',
        to: 'review'
      },
      {
        id: 'review-book',
        from: 'review',
        to: 'book',
        label: 'Approved'
      }
    ]
  }
}

export const addressMapArtifactFixture: AddressMapArtifact = {
  id: 'artifact-address-map-route',
  type: 'address-map',
  title: 'Service visit route',
  createdAt: '2026-07-04T12:10:00.000Z',
  data: {
    center: {
      latitude: 43.6532,
      longitude: -79.3832
    },
    zoom: 12,
    markers: [
      {
        id: 'office',
        label: 'Office',
        address: '100 Queen St W, Toronto, ON',
        coordinate: {
          latitude: 43.6535,
          longitude: -79.3839
        }
      },
      {
        id: 'site',
        label: 'Client site',
        address: '1 Front St E, Toronto, ON',
        coordinate: {
          latitude: 43.6487,
          longitude: -79.3715
        }
      }
    ]
  }
}

export const heatmapArtifactFixture: HeatmapArtifact = {
  id: 'artifact-heatmap-engagement',
  type: 'heatmap',
  title: 'Page engagement heatmap',
  createdAt: '2026-07-04T12:15:00.000Z',
  data: {
    bounds: {
      width: 1440,
      height: 1200
    },
    points: [
      {
        x: 240,
        y: 180,
        intensity: 0.84
      },
      {
        x: 860,
        y: 420,
        intensity: 0.62
      },
      {
        x: 1120,
        y: 760,
        intensity: 0.37
      }
    ]
  }
}

export const routeMapArtifactFixture: RouteMapArtifact = {
  id: 'artifact-route-map-canvass',
  type: 'route-map',
  title: 'Door-to-door canvass route',
  createdAt: '2026-07-04T12:20:00.000Z',
  data: {
    center: {
      latitude: 43.6532,
      longitude: -79.3832
    },
    zoom: 13,
    stops: [
      {
        id: 'start',
        label: 'Office',
        address: '100 Queen St W, Toronto, ON',
        coordinate: {
          latitude: 43.6535,
          longitude: -79.3839
        },
        sequence: 0
      },
      {
        id: 'stop-1',
        label: 'Prospect 1',
        address: '55 Gould St, Toronto, ON',
        coordinate: {
          latitude: 43.6577,
          longitude: -79.3803
        },
        sequence: 1
      },
      {
        id: 'stop-2',
        label: 'Prospect 2',
        address: '1 Front St E, Toronto, ON',
        coordinate: {
          latitude: 43.6487,
          longitude: -79.3715
        },
        sequence: 2
      }
    ],
    routes: [
      {
        id: 'route-main',
        label: 'Main route',
        stopIds: ['start', 'stop-1', 'stop-2'],
        distanceMeters: 3600,
        durationSeconds: 1080,
        path: [
          {
            latitude: 43.6535,
            longitude: -79.3839
          },
          {
            latitude: 43.6577,
            longitude: -79.3803
          },
          {
            latitude: 43.6487,
            longitude: -79.3715
          }
        ]
      }
    ]
  }
}

export const territoryMapArtifactFixture: TerritoryMapArtifact = {
  id: 'artifact-territory-map-service',
  type: 'territory-map',
  title: 'Downtown service territories',
  createdAt: '2026-07-04T12:25:00.000Z',
  data: {
    center: {
      latitude: 43.6532,
      longitude: -79.3832
    },
    zoom: 12,
    territories: [
      {
        id: 'core-west',
        label: 'Core west',
        color: '#2563eb',
        intensity: 0.72,
        coordinates: [
          {
            latitude: 43.6618,
            longitude: -79.4022
          },
          {
            latitude: 43.6611,
            longitude: -79.3824
          },
          {
            latitude: 43.6468,
            longitude: -79.3785
          },
          {
            latitude: 43.6455,
            longitude: -79.3976
          }
        ]
      }
    ]
  }
}

export const blueprintArtifactFixture: BlueprintArtifact = {
  id: 'artifact-blueprint-kiosk',
  type: 'blueprint',
  title: 'Retail kiosk floor plan',
  createdAt: '2026-07-04T12:30:00.000Z',
  data: {
    unit: 'centimeter',
    scale: '1:20',
    bounds: {
      width: 480,
      height: 300
    },
    elements: [
      {
        id: 'north-wall',
        kind: 'wall',
        label: 'North wall',
        measurement: 480,
        points: [
          {
            x: 0,
            y: 0
          },
          {
            x: 480,
            y: 0
          }
        ]
      },
      {
        id: 'counter-zone',
        kind: 'area',
        label: 'Counter zone',
        points: [
          {
            x: 40,
            y: 40
          },
          {
            x: 220,
            y: 40
          },
          {
            x: 220,
            y: 110
          },
          {
            x: 40,
            y: 110
          }
        ]
      },
      {
        id: 'entry-door',
        kind: 'door',
        label: 'Entry door',
        measurement: 90,
        points: [
          {
            x: 360,
            y: 300
          },
          {
            x: 450,
            y: 300
          }
        ]
      }
    ]
  }
}

export const visualArtifactFixtures: VisualArtifact[] = [
  threeDModelArtifactFixture,
  schematicArtifactFixture,
  addressMapArtifactFixture,
  heatmapArtifactFixture,
  routeMapArtifactFixture,
  territoryMapArtifactFixture,
  blueprintArtifactFixture
]
